import { SynonymGroup } from "@/models/synonym-group/synonymGroup";
import { SynonymPreferences } from "@/models/users/UserPreference";

export type SynonymMatch = {
  canonicalTerm: string;
  groupId: string;
};

export type SynonymDictionary = Map<string, SynonymMatch[]>;

const LETTER_PATTERN = /\p{L}/u;
const NUMBER_PATTERN = /\p{N}/u;

type WordCharClass = "letter" | "number" | "other";

const getWordCharClass = (char: string): WordCharClass => {
  if (LETTER_PATTERN.test(char)) {
    return "letter";
  }
  if (NUMBER_PATTERN.test(char)) {
    return "number";
  }
  return "other";
};

const isHighSurrogate = (value: number): boolean =>
  value >= 0xd800 && value <= 0xdbff;
const isLowSurrogate = (value: number): boolean =>
  value >= 0xdc00 && value <= 0xdfff;

const getCodePointStartIndex = (text: string, index: number): number => {
  if (index <= 0 || index >= text.length) {
    return index;
  }

  const currentCodeUnit = text.charCodeAt(index);
  const previousCodeUnit = text.charCodeAt(index - 1);
  if (isLowSurrogate(currentCodeUnit) && isHighSurrogate(previousCodeUnit)) {
    return index - 1;
  }
  return index;
};

const getCodePointEndIndex = (text: string, index: number): number => {
  if (index < 0 || index >= text.length) {
    return index;
  }

  const firstCodeUnit = text.charCodeAt(index);
  const secondCodeUnit = text.charCodeAt(index + 1);
  if (isHighSurrogate(firstCodeUnit) && isLowSurrogate(secondCodeUnit)) {
    return index + 2;
  }
  return index + 1;
};

const getWordCharClassAt = (text: string, index: number): WordCharClass => {
  if (index < 0 || index >= text.length) {
    return "other";
  }

  const startIndex = getCodePointStartIndex(text, index);
  const codePoint = text.codePointAt(startIndex);
  if (codePoint === undefined) {
    return "other";
  }
  return getWordCharClass(String.fromCodePoint(codePoint));
};

/**
 * Builds a synonym dictionary from synonym groups and user preferences.
 * Filters groups based on user preferences and creates a lookup map.
 */
export function buildSynonymDictionary(
  groups: SynonymGroup[] | undefined,
  preferences: SynonymPreferences | undefined
): SynonymDictionary {
  const dictionary: SynonymDictionary = new Map();

  if (!groups || !preferences) {
    return dictionary;
  }

  // Only process if synonym matching is globally active
  if (!preferences.synonymMatchingActive) {
    return dictionary;
  }

  for (const group of groups) {
    // Check if this group is active for the user
    const isGroupActive = preferences.synonymGroupsActive[group.id] === true;

    if (!isGroupActive || !group.isActive) {
      continue;
    }

    const canonicalTerm = group.canonicalTerm?.trim();
    if (!canonicalTerm) {
      continue;
    }

    const match: SynonymMatch = {
      canonicalTerm,
      groupId: group.id,
    };

    // Add all active synonyms to the dictionary
    for (const synonym of group.synonyms ?? []) {
      if (!synonym.active) {
        continue;
      }

      const original = synonym.original?.trim().toLowerCase();
      const normalized = synonym.normalized?.trim().toLowerCase();

      if (original) {
        const existing = dictionary.get(original) || [];
        // Avoid duplicates
        if (!existing.some((m) => m.canonicalTerm === canonicalTerm)) {
          dictionary.set(original, [...existing, match]);
        }
      }

      if (normalized && normalized !== original) {
        const existing = dictionary.get(normalized) || [];
        if (!existing.some((m) => m.canonicalTerm === canonicalTerm)) {
          dictionary.set(normalized, [...existing, match]);
        }
      }
    }
  }

  return dictionary;
}

/**
 * Gets the word boundaries at the given cursor position.
 * Returns start and end positions of the word containing the cursor.
 */
export function getWordBoundaries(
  text: string,
  cursorPosition: number
): { start: number; end: number; word: string } {
  if (cursorPosition < 0 || cursorPosition > text.length) {
    return { start: cursorPosition, end: cursorPosition, word: "" };
  }

  const currentIndex = getCodePointStartIndex(text, cursorPosition);
  const previousIndex = getCodePointStartIndex(text, cursorPosition - 1);
  const currentClass = getWordCharClassAt(text, currentIndex);
  const previousClass = getWordCharClassAt(text, previousIndex);
  const referenceClass =
    currentClass !== "other"
      ? currentClass
      : previousClass !== "other"
        ? previousClass
        : "other";

  if (referenceClass === "other") {
    return { start: cursorPosition, end: cursorPosition, word: "" };
  }

  // Find word start (go backwards until we hit a boundary or switch class)
  let start = cursorPosition;
  while (
    start > 0 &&
    getWordCharClassAt(text, start - 1) === referenceClass
  ) {
    start = getCodePointStartIndex(text, start - 1);
  }

  // Find word end (go forwards until we hit a boundary or switch class)
  let end = cursorPosition;
  while (
    end < text.length &&
    getWordCharClassAt(text, end) === referenceClass
  ) {
    end = getCodePointEndIndex(text, end);
  }

  const word = text.slice(start, end).toLowerCase();

  return { start, end, word };
}

/**
 * Extracts the current word at the cursor position.
 */
export function findCurrentWord(
  text: string,
  cursorPosition: number
): { start: number; end: number; word: string } {
  return getWordBoundaries(text, cursorPosition);
}

/**
 * Finds synonym matches for the given word.
 * Returns an array of canonical terms that match the word.
 */
export function findSynonymMatches(
  word: string,
  dictionary: SynonymDictionary
): SynonymMatch[] {
  if (!word || word.length === 0) {
    return [];
  }

  const normalizedWord = word.toLowerCase().trim();
  const matches = dictionary.get(normalizedWord) || [];

  // Remove duplicates based on canonicalTerm
  const uniqueMatches = Array.from(
    new Map(matches.map((m) => [m.canonicalTerm, m])).values()
  );

  return uniqueMatches;
}
